// Definitions for the newer-feature tools (see coachTools.ts). Pure data so web
// and native voice calls can import them too.

export type ToolSchema = {
  type: 'object'
  properties: Record<
    string,
    { type: string; description: string; enum?: string[]; items?: { type: string } }
  >
  required: string[]
}
export type CoachToolDef = { name: string; description: string; parameters: ToolSchema }

const none: ToolSchema = { type: 'object', properties: {}, required: [] }
const str = (description: string) => ({ type: 'string', description })

export const COACH_TOOLS: CoachToolDef[] = [
  {
    name: 'get_everyday',
    description:
      "The user's Everyday view: habits due today (done or not, with streaks; number habits with today's total, target and how much is left), habits paused by a frozen goal, open quick tasks, and this week's habit consistency.",
    parameters: none,
  },
  {
    name: 'mark_done',
    description:
      'Mark a task, quick task or habit done for today. Only when the user says they just did it.',
    parameters: {
      type: 'object',
      properties: { task_id: str('Task or habit id') },
      required: ['task_id'],
    },
  },
  {
    name: 'mark_not_done',
    description: "Undo today's completion of a task, quick task or habit (marked by mistake).",
    parameters: {
      type: 'object',
      properties: { task_id: str('Task or habit id') },
      required: ['task_id'],
    },
  },
  {
    name: 'log_habit_amount',
    description:
      'Add an amount to a number habit for today (e.g. 350 kcal, 2 glasses of water, 1500 steps). get_everyday shows number habits with their unit and progress. Only when the user tells you what they had or did; the day counts as done when the target is met (or, for an "at most" habit, while they stay under it).',
    parameters: {
      type: 'object',
      properties: {
        habit_id: str('Number habit id'),
        amount: { type: 'number', description: "Amount in the habit's unit, positive" },
        note: str('Optional short note, e.g. "chicken salad"'),
      },
      required: ['habit_id', 'amount'],
    },
  },
  {
    name: 'start_habit_timer',
    description:
      'Start the timer on a minutes habit (a number habit in minutes, e.g. meditate, read, plank; get_habits shows a running timer). A plain check-off habit can be converted with make_timer_target once the user agrees. With `minutes` it is a countdown that logs itself when it ends; without, an open-ended stopwatch that stop_habit_timer logs. Replaces a timer already running on that habit. Only when the user asks to start one.',
    parameters: {
      type: 'object',
      properties: {
        habit_id: str('Minutes habit id'),
        minutes: {
          type: 'number',
          description: 'Countdown length in minutes; omit for open-ended',
        },
        make_timer_target: {
          type: 'number',
          description:
            'Only after the user agreed: turns a plain check-off habit into a minutes habit with this daily target, then starts the timer',
        },
      },
      required: ['habit_id'],
    },
  },
  {
    name: 'stop_habit_timer',
    description:
      "Stop a running habit timer and log the elapsed whole minutes (rounded, at least 1; a run under 30 seconds is discarded). Counts the day as done if the target is met. Only when the user asks to stop or says they've finished.",
    parameters: {
      type: 'object',
      properties: { habit_id: str('Minutes habit id') },
      required: ['habit_id'],
    },
  },
  {
    name: 'raise_habit_target',
    description:
      'Set a new daily target for a number habit, starting tomorrow (today keeps the old target, so today\'s progress is unaffected). Only after the user agrees to a raise, e.g. one from "ready to level up".',
    parameters: {
      type: 'object',
      properties: {
        habit_id: str('Number habit id'),
        target: { type: 'number', description: "New target in the habit's unit, positive" },
      },
      required: ['habit_id', 'target'],
    },
  },
  {
    name: 'set_habit_auto_progress',
    description:
      'Turn on, change or clear automatic progression for a number habit ("add 2 kg every week"). Each period (7 or 14 days) the target goes up by `step` (down for at-most habits) if enough scheduled days hit the target, otherwise it holds; the user gets a nudge either way. Only when the user asks.',
    parameters: {
      type: 'object',
      properties: {
        habit_id: str('Number habit id'),
        clear: { type: 'boolean', description: 'true to turn auto-progress off' },
        step: { type: 'number', description: "Change per period in the habit's unit, positive" },
        every_days: { type: 'number', description: '7 (weekly, default) or 14' },
        cap: {
          type: 'number',
          description: 'Optional value to stop at (max, or goal for at-most)',
        },
        min_hit_percent: {
          type: 'number',
          description: 'Share of days that must hit, 1-100 (default 70)',
        },
      },
      required: ['habit_id'],
    },
  },
  {
    name: 'set_task_status',
    description:
      'Move a task between Not started and In progress (kanban status). To finish a task use complete_task. Only when the user asks.',
    parameters: {
      type: 'object',
      properties: {
        task_id: str('Task id'),
        status: { type: 'string', enum: ['backlog', 'in-progress'], description: 'New status' },
      },
      required: ['task_id', 'status'],
    },
  },
  {
    name: 'move_task_to_target',
    description:
      "Move the user's own tasks (from the inbox or another goal) under a target of a goal's plan, keeping their notes, tags, due date and completion. Get task ids from get_inbox_tasks or get_goal_plan and the target id from get_goal_plan. If any id isn't theirs, nothing moves. Use move_tasks_to_goal to let Goaltree pick the target.",
    parameters: {
      type: 'object',
      properties: {
        task_ids: { type: 'array', items: { type: 'string' }, description: 'Task ids to move' },
        target_id: str('Destination target id (from get_goal_plan)'),
      },
      required: ['task_ids', 'target_id'],
    },
  },
  {
    name: 'freeze_goal',
    description:
      "Freeze (pause) a goal: its tasks leave Today, its habits pause and frozen days don't break streaks. Optionally thaw automatically after `days`. Use when the user wants a break from a goal, is overwhelmed, travelling or sick.",
    parameters: {
      type: 'object',
      properties: {
        goal_id: str('Goal id'),
        days: {
          type: 'number',
          description: 'Thaw after this many days; omit to freeze until unfrozen',
        },
      },
      required: ['goal_id'],
    },
  },
  {
    name: 'unfreeze_goal',
    description: 'Unfreeze a frozen goal so its tasks and habits come back.',
    parameters: { type: 'object', properties: { goal_id: str('Goal id') }, required: ['goal_id'] },
  },
  {
    name: 'get_growth',
    description:
      "The user's badges (count, most recent with dates), canopy, and each habit's growth level (seed → ancient) with check-ins to the next level.",
    parameters: none,
  },
  {
    name: 'get_habit_stats',
    description:
      "One habit's history: total check-ins, current and best streak, strength (0-100%, a miss dents it rather than resetting) and its trend, consistency overall and last 30 days.",
    parameters: {
      type: 'object',
      properties: { habit_id: str('Habit id') },
      required: ['habit_id'],
    },
  },
  {
    name: 'list_groves',
    description:
      "The user's groves (small accountability groups): members, how many checked in today, grove streak and unread messages.",
    parameters: none,
  },
  {
    name: 'get_grove_chat',
    description: "Recent messages in one of the user's groves.",
    parameters: {
      type: 'object',
      properties: { grove_id: str('Grove id') },
      required: ['grove_id'],
    },
  },
  {
    name: 'send_grove_message',
    description:
      'Post a message to a grove AS THE USER. Only when they explicitly ask you to post or reply there; read the exact text back first if you drafted it.',
    parameters: {
      type: 'object',
      properties: { grove_id: str('Grove id'), message: str('Message text, in their words') },
      required: ['grove_id', 'message'],
    },
  },
  {
    name: 'get_momentum',
    description:
      "Recent progress from the user's friends (Momentum feed): streak milestones, finished goals, badges, check-ins, with ids for boosting.",
    parameters: none,
  },
  {
    name: 'boost',
    description:
      "Cheer on a friend's Momentum update. kind: water (any), sun (any), fire (streaks), summit (milestones and goals only). Only when the user asks.",
    parameters: {
      type: 'object',
      properties: {
        event_id: str('Momentum update id from get_momentum'),
        kind: {
          type: 'string',
          description: 'Boost kind',
          enum: ['water', 'sun', 'fire', 'summit'],
        },
      },
      required: ['event_id', 'kind'],
    },
  },
  {
    name: 'get_fitness',
    description:
      'Wearable data synced from Fitbit/Strava for the last week: steps, sleep, active minutes, workouts. Empty if nothing is connected.',
    parameters: none,
  },
  {
    name: 'find_templates',
    description:
      "Find ready-made goal plans: 'Classics' inspired by well-known books (e.g. Atomic Habits, Deep Work, Getting Things Done, The 7 Habits, The Miracle Morning, Think and Grow Rich, Franklin's virtues, Stoic practice) and popular community plans. Returns titles with links. Use when the user wants to start something new, asks for a structured approach, or names a book/method.",
    parameters: {
      type: 'object',
      properties: {
        query: str('Optional topic, book or method to match, e.g. "habits", "focus", "money"'),
      },
      required: [],
    },
  },
  {
    name: 'set_habit_time_today',
    description:
      'Move one habit to another time for TODAY ONLY (its usual time stays for other days; the user can undo). Use to make room around an event, and only after the user agrees to the specific new time.',
    parameters: {
      type: 'object',
      properties: {
        habit_id: str('Habit id'),
        time: str('New time today, 24-hour HH:MM, e.g. "18:00"'),
      },
      required: ['habit_id', 'time'],
    },
  },
]

export const COACH_TOOL_NAMES = new Set(COACH_TOOLS.map((t) => t.name))

// Said in coach prompts that carry the user's schedule (chat and calls).
export const EVENTS_COACH_RULE =
  'Events are things that happen at a set time (appointments, meetings, flights). They are read-only context: plan the user\'s tasks and habits around them. If an event clashes with or crowds a habit time or a Do today task (count the travel estimate when there is one), mention it once, in one line, and suggest ONE concrete change, such as moving a habit for today with set_habit_time_today after they agree. Never change, move, delete or mark events done (they finish on their own) and never touch Google Calendar. Travel estimates are rough: always say "about" and never give them as exact.'

// Coach chat and calls only (not MCP): commitments the coach follows up on, and
// scheduled check-in calls.
export const COMMITMENT_TOOLS: CoachToolDef[] = [
  {
    name: 'track_commitment',
    description:
      'Save or resolve a commitment the user makes to you. add: when they commit to something specific and time-bound ("gym Tuesday and Thursday", "call the dentist tomorrow", "in bed by 11 this week"); not vague wishes, and not your suggestions unless they agreed. Call it once, then confirm in one short line ("Noted: gym Tue + Thu"). kept / missed / moved: when they tell you how an open commitment went (ids are in your instructions); moved needs new days, missed takes a reason if they gave one. remove: they ask you to forget it.',
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          description: 'What to do',
          enum: ['add', 'kept', 'missed', 'moved', 'remove'],
        },
        text: str('add: the commitment, short, in the user\'s language, e.g. "Gym"'),
        days: {
          type: 'array',
          items: { type: 'string' },
          description:
            'add / moved: the day(s) it is due, each YYYY-MM-DD, +N (days from today: +0 today, +1 tomorrow) or a weekday name (mon…sun = the next one after today). "This week" = each remaining day. Max 7.',
        },
        habit_or_task_id: str('add: id of a habit or task it matches, if any'),
        commitment_id: str('kept / missed / moved / remove: the commitment id'),
        reason: {
          type: 'string',
          description: 'missed: why, if they said',
          enum: ['tooBig', 'noTime', 'notNow', 'wrongDay', 'tired', 'restDay', 'unwell', 'other'],
        },
      },
      required: ['action'],
    },
  },
  {
    name: 'set_checkin_calls',
    description:
      'Schedule in-app check-in calls from you (Pro only), e.g. "call me Tuesdays and Thursdays at 6:30": you ring them at that local time to go over their commitments. At most 3 days a week. off: true stops them. Read the days and time back in one line once it succeeds.',
    parameters: {
      type: 'object',
      properties: {
        days: {
          type: 'array',
          items: { type: 'string' },
          description: 'Weekday names, e.g. ["tue","thu"] (max 3)',
        },
        time: str('Local time, 24-hour HH:MM, e.g. "18:30"'),
        off: { type: 'boolean', description: 'true to stop scheduled check-in calls' },
      },
      required: [],
    },
  },
]
export const COMMITMENT_TOOL_NAMES = new Set(COMMITMENT_TOOLS.map((t) => t.name))

// Task tools only voice calls get from the server: the text coach and MCP have
// their own versions of these.
export const CALL_TASK_TOOLS: CoachToolDef[] = [
  {
    name: 'add_tasks_to_goal',
    description:
      "Add one or more new tasks to one of the user's existing goals (they go under its current milestone). Use when they ask to add something to a goal or plan; for a loose to-do not tied to a goal use create_quick_task. The result has the new task ids, e.g. for add_to_today.",
    parameters: {
      type: 'object',
      properties: {
        goal_id: str('Goal id from list_goals or the goals in your instructions'),
        titles: { type: 'array', items: { type: 'string' }, description: 'Task titles, short' },
      },
      required: ['goal_id', 'titles'],
    },
  },
  {
    name: 'list_inbox_tasks',
    description: "The user's open inbox/quick tasks with their ids. Use before moving them.",
    parameters: none,
  },
  {
    name: 'move_tasks_to_goal',
    description:
      'Move existing inbox/quick tasks into a goal (they leave the inbox). Get ids from list_inbox_tasks and the goal id from list_goals.',
    parameters: {
      type: 'object',
      properties: {
        task_ids: { type: 'array', items: { type: 'string' }, description: 'Inbox task ids' },
        goal_id: str('Goal id'),
      },
      required: ['task_ids', 'goal_id'],
    },
  },
]

// Run server-side (coachTools.run) during a call.
export const CALL_SERVER_TOOL_NAMES = new Set([
  ...COACH_TOOL_NAMES,
  ...COMMITMENT_TOOL_NAMES,
  ...CALL_TASK_TOOLS.map((t) => t.name),
])

// Realtime voice-call format. Voice already has mark_task_done / mark_task_not_done.
export const VOICE_COACH_TOOLS = [
  ...COACH_TOOLS.filter((t) => t.name !== 'mark_done' && t.name !== 'mark_not_done'),
  ...COMMITMENT_TOOLS,
  ...CALL_TASK_TOOLS,
].map((t) => ({ type: 'function' as const, ...t }))

// What a call sends back to the model for a server tool's text result, so a
// failure never reads as success.
export function callToolOutput(result: string) {
  return /^(Error:|Unknown tool )/.test(result)
    ? { ok: false, error: result.replace(/^Error:\s*/, '') }
    : { ok: true, result }
}

// Said in every coach prompt (chat and calls).
export const ACTION_HONESTY_RULE =
  "Never say you added, created, moved, scheduled, completed or changed anything unless the matching tool call in this turn succeeded. If you are only suggesting it, ask (\"Want me to add that?\") rather than saying it's done. If a tool returns an error or ok: false, say plainly it didn't work and why, and offer to try again. Don't promise to do something later: call the tool now or say you can't."

// Call pace, handled by the call client itself: it switches the live session
// (speech speed + instructions) and saves the pref.
export const CALL_PACE_TOOL = {
  type: 'function' as const,
  name: 'set_call_pace',
  description:
    'Switch this call to quick mode (they say they are in a rush, want a quick one, short on time) or back to normal pace (they ask you to slow down or have more time).',
  parameters: {
    type: 'object' as const,
    properties: {
      quick: { type: 'boolean', description: 'true for a quick call, false for normal pace' },
    },
    required: ['quick'],
  },
}
export const QUICK_CALL_SPEED = 1.3
// Live-call speech speed: a character's base pace, sped up (never slowed) by
// Quick call, kept within what the realtime voice still sounds natural at.
export function callSpeed(base: number, quick: boolean) {
  const s = quick ? Math.max(QUICK_CALL_SPEED, base * 1.15) : base
  return Math.min(1.5, Math.max(0.7, s))
}
export const QUICK_CALL_INSTRUCTIONS =
  '\nQUICK CALL: they are in a rush. Answer in one short sentence. Skip small talk, greetings and recaps. Run a rapid-fire check-in: what is done, what is next, anything to change. Still use your tools as normal.'
